import { FaceDetectionResult } from "./FaceDetector";

/**
 * Passive natural-motion liveness detector.
 *
 * Replaces the earlier sequential "do BLINK, then TURN_HEAD, then OPEN_MOUTH"
 * choreography with a single continuous scan window: the user is simply told
 * to "blink and look around naturally" while the detector watches for signs
 * of a live face (a genuine blink, or natural head movement) without
 * commanding specific actions in order. This mirrors a passive liveness scan
 * rather than a step-by-step obstacle course.
 *
 * Pass criteria: BOTH a genuine blink AND a natural head-yaw excursion must
 * be detected during the window, in either order — one signal alone is too
 * easy for a held-up photo or a short video replay to trigger by accident
 * (e.g. incidental camera/device motion registering as "head-yaw" with no
 * live person present). Requiring both raises the bar without going back to
 * commanded, sequential actions.
 */

const BLINK_THRESHOLD = 0.25;
const BLINK_HOLD_FRAMES = 2;
const YAW_MOTION_THRESHOLD_DEG = 6; // small natural head movement, not a commanded turn

export interface ScanProgress {
  faceDetected: boolean;
  blinkDetected: boolean;
  motionDetected: boolean;
  livenessConfirmed: boolean;
}

export class ActionDetector {
  private blinkHoldCount = 0;
  private blinkDetected = false;
  private motionDetected = false;
  // Running min/max yaw seen so far in the scan, not a single fixed
  // first-frame baseline (see check() below for why).
  private minYaw: number | null = null;
  private maxYaw: number | null = null;

  check(result: FaceDetectionResult): ScanProgress {
    if (!result.faceDetected) {
      return this.buildProgress(false);
    }

    const bs = result.blendshapes;
    const blinkConf = ((bs.get("eyeBlinkLeft") || 0) + (bs.get("eyeBlinkRight") || 0)) / 2;

    if (!this.blinkDetected) {
      if (blinkConf >= BLINK_THRESHOLD) {
        this.blinkHoldCount++;
        if (this.blinkHoldCount >= BLINK_HOLD_FRAMES) {
          this.blinkDetected = true;
        }
      } else {
        this.blinkHoldCount = 0;
      }
    }

    if (!this.motionDetected) {
      const yaw = this.getHeadYaw(result.transformMatrix);
      // A single first-frame baseline used to anchor this check — but the
      // scan's first frame is essentially never a settled, neutral pose
      // (camera/UI is still initializing, the user hasn't found center
      // yet), and that baseline was never re-anchored for the rest of the
      // window. A genuine head turn that happened to move TOWARD that
      // arbitrary first reading, rather than away from it, read as a small
      // delta and was missed — reported live as "blink saved, motion not,"
      // on an ordinary fast look-around. Tracking the running min/max yaw
      // and firing on their SPREAD instead fixes this: it's baseline-free,
      // so it doesn't matter which frame happened to come first or which
      // direction the real turn went — only that a genuine range of motion
      // occurred somewhere across the window.
      if (this.minYaw === null || yaw < this.minYaw) this.minYaw = yaw;
      if (this.maxYaw === null || yaw > this.maxYaw) this.maxYaw = yaw;
      if (this.maxYaw - this.minYaw >= YAW_MOTION_THRESHOLD_DEG) {
        this.motionDetected = true;
      }
    }

    return this.buildProgress(true);
  }

  /** Snapshot of what's been detected so far — for reading the result after
   * the scan loop has already ended (e.g. to report alongside the submit
   * payload), without needing another detection frame. */
  getProgress(): ScanProgress {
    return this.buildProgress(true);
  }

  private buildProgress(faceDetected: boolean): ScanProgress {
    return {
      faceDetected,
      blinkDetected: this.blinkDetected,
      motionDetected: this.motionDetected,
      livenessConfirmed: this.blinkDetected && this.motionDetected,
    };
  }

  private getHeadYaw(matrix: number[] | null): number {
    if (!matrix || matrix.length < 16) return 0;
    return Math.atan2(-matrix[2], matrix[0]) * (180 / Math.PI);
  }
}
